import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  MapPin,
  Phone,
  Mail,
  MessageCircle,
  ArrowUpRight,
  Loader2,
} from "lucide-react";
import { CONTACT } from "@/constants/testIds";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  "Website Development",
  "Mobile / Web App",
  "Branding & Identity",
  "Creative Ads",
  "Digital Marketing",
  "SEO & Content",
  "AI Automation",
  "Hosting & Maintenance",
  "Other",
];

const PHONE = "7975259104";
const PHONE_DISPLAY = "+91 79752 59104";
const PHONE_TEL = "+917975259104";
const WA_MESSAGE = "Hello HR ENTERPRISES,\n\nI visited your website and would like to discuss my project. Please let me know a convenient time to connect.";
const WA_LINK = `https://wa.me/91${PHONE}?text=${encodeURIComponent(WA_MESSAGE)}`;
const EMAIL = "hrgroupsenterprises@gmail.com";
const EMAIL_SUBJECT = "Website Inquiry – HR ENTERPRISES";
const EMAIL_BODY = `Hello HR ENTERPRISES Team,

I would like to discuss a project regarding:

Business Name:
Service Required:
Timeline:
Budget:

Looking forward to hearing from you.

Regards,`;
const MAILTO = `mailto:${EMAIL}?subject=${encodeURIComponent(EMAIL_SUBJECT)}&body=${encodeURIComponent(EMAIL_BODY)}`;

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState({});

  const update = (k) => (e) => {
    const value = e.target?.value ?? e;
    setForm((s) => ({ ...s, [k]: value }));
    // Clear error on change; validate email inline
    setErrors((prev) => {
      const next = { ...prev };
      if (k === "email") {
        if (value && !isValidEmail(value.trim())) next.email = "Enter a valid email address.";
        else delete next.email;
      } else if (k === "name" || k === "message") {
        if (value.trim()) delete next[k];
      }
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    const localErrors = {};
    if (!form.name.trim()) localErrors.name = "Please enter your name.";
    if (!form.email.trim()) localErrors.email = "Please enter your email.";
    else if (!isValidEmail(form.email.trim()))
      localErrors.email = "Enter a valid email address.";
    if (!form.message.trim())
      localErrors.message = "Please share a short project brief.";
    if (Object.keys(localErrors).length) {
      setErrors(localErrors);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        company: form.company.trim() || null,
        service: form.service || null,
        message: form.message.trim(),
      };
      await axios.post(`${API}/contacts`, payload);
      toast.success("Inquiry received. We'll reach out within one business day.");
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        service: "",
        message: "",
      });
      setErrors({});
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or WhatsApp us.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3.5 text-[14px] text-white placeholder:text-zinc-500 outline-none transition-colors duration-300 focus:border-[var(--gold)]/60 focus:bg-white/[0.05]";

  return (
    <section
      id="contact"
      data-testid={CONTACT.root}
      className="relative py-24 lg:py-36"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left — info */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
            className="lg:col-span-5"
          >
            <div className="mb-4 flex items-center gap-3">
              <div className="h-px w-8 bg-[var(--gold)]/60" />
              <span className="font-mono-label text-[var(--gold)]">
                05 · Contact
              </span>
            </div>
            <h2 className="font-display text-4xl leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-[56px]">
              Let&apos;s<br />
              <span className="silver-gradient-text">build.</span>
            </h2>
            <p className="mt-6 max-w-md text-[15px] leading-relaxed text-zinc-400">
              Tell us your vision. We&apos;ll take care of the rest. Usually
              replying within 24 hours. Often much sooner.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={WA_LINK}
                target="_blank"
                rel="noreferrer"
                data-testid={CONTACT.whatsapp}
                className="group flex items-center justify-between gap-4 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 transition-all duration-300 hover:border-[var(--gold)]/40 hover:bg-white/[0.04]"
              >
                <div className="flex items-center gap-4">
                  <div className="grid h-11 w-11 place-items-center rounded-lg border border-[var(--gold)]/30 bg-[var(--gold)]/10 text-[var(--gold-bright)]">
                    <MessageCircle className="h-5 w-5" strokeWidth={1.7} />
                  </div>
                  <div>
                    <div className="font-mono-label text-zinc-500">WhatsApp</div>
                    <div className="mt-1 font-display text-[17px] font-medium text-white">
                      Chat with us instantly
                    </div>
                  </div>
                </div>
                <ArrowUpRight className="h-4 w-4 text-zinc-500 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[var(--gold-bright)]" />
              </a>

              <a
                href={`tel:${PHONE_TEL}`}
                data-testid={CONTACT.phoneLink}
                className="group flex items-center justify-between gap-4 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 transition-all duration-300 hover:border-[var(--gold)]/40 hover:bg-white/[0.04] active:scale-[0.99]"
              >
                <div className="flex items-center gap-4">
                  <div className="grid h-11 w-11 place-items-center rounded-lg border border-white/10 bg-white/[0.03] text-zinc-300">
                    <Phone className="h-5 w-5" strokeWidth={1.7} />
                  </div>
                  <div>
                    <div className="font-mono-label text-zinc-500">Call Now</div>
                    <div className="mt-1 font-display text-[17px] font-medium text-white">
                      {PHONE_DISPLAY}
                    </div>
                  </div>
                </div>
                <ArrowUpRight className="h-4 w-4 text-zinc-500 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[var(--gold-bright)]" />
              </a>

              <a
                href={MAILTO}
                data-testid={CONTACT.emailLink}
                className="group flex items-center justify-between gap-4 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 transition-all duration-300 hover:border-[var(--gold)]/40 hover:bg-white/[0.04] active:scale-[0.99]"
              >
                <div className="flex items-center gap-4">
                  <div className="grid h-11 w-11 place-items-center rounded-lg border border-white/10 bg-white/[0.03] text-zinc-300">
                    <Mail className="h-5 w-5" strokeWidth={1.7} />
                  </div>
                  <div>
                    <div className="font-mono-label text-zinc-500">Email Us</div>
                    <div className="mt-1 break-all font-display text-[15px] font-medium text-white sm:text-[16px]">
                      {EMAIL}
                    </div>
                  </div>
                </div>
                <ArrowUpRight className="h-4 w-4 text-zinc-500 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[var(--gold-bright)]" />
              </a>

              <div className="flex items-start gap-4 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5">
                <div className="grid h-11 w-11 place-items-center rounded-lg border border-white/10 bg-white/[0.03] text-zinc-300">
                  <MapPin className="h-5 w-5" strokeWidth={1.7} />
                </div>
                <div>
                  <div className="font-mono-label text-zinc-500">Studio</div>
                  <div className="mt-1 font-display text-[15px] font-medium text-white">
                    Bengaluru, Karnataka · India
                  </div>
                  <div className="mt-1 text-[13px] text-zinc-500">
                    By appointment only.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5">
                  <div className="font-mono-label text-zinc-500">Business hours</div>
                  <div className="mt-2 font-display text-[15px] font-medium text-white">
                    Mon – Sat
                  </div>
                  <div className="mt-1 text-[12.5px] text-zinc-500">
                    10:00 – 19:00 IST
                  </div>
                </div>
                <div className="rounded-2xl border border-[var(--gold)]/20 bg-[var(--gold)]/[0.04] p-5">
                  <div className="font-mono-label text-[var(--gold)]">Response time</div>
                  <div className="mt-2 font-display text-[15px] font-medium text-white">
                    Within 24 hours
                  </div>
                  <div className="mt-1 text-[12.5px] text-zinc-500">
                    Usually much faster.
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right — form */}
          <motion.form
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1], delay: 0.1 }}
            data-testid={CONTACT.form}
            onSubmit={submit}
            noValidate
            className="relative rounded-3xl border border-white/[0.08] bg-[#0d0d0f] p-7 lg:col-span-7 lg:p-10"
          >
            <div className="absolute -inset-px rounded-3xl bg-gradient-to-b from-[var(--gold)]/10 via-transparent to-transparent opacity-40" style={{ pointerEvents: "none" }} />
            <div className="relative grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <label className="font-mono-label mb-2 block text-zinc-500">
                  Name*
                </label>
                <input
                  data-testid={CONTACT.name}
                  className={`${inputCls} ${errors.name ? "border-red-400/60" : ""}`}
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your full name"
                  autoComplete="name"
                  required
                />
                {errors.name && (
                  <p className="mt-1.5 text-[11.5px] text-red-400">{errors.name}</p>
                )}
              </div>
              <div className="sm:col-span-1">
                <label className="font-mono-label mb-2 block text-zinc-500">
                  Email*
                </label>
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  data-testid={CONTACT.email}
                  className={`${inputCls} ${errors.email ? "border-red-400/60" : ""}`}
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@company.com"
                  required
                />
                {errors.email && (
                  <p className="mt-1.5 text-[11.5px] text-red-400">{errors.email}</p>
                )}
              </div>
              <div className="sm:col-span-1">
                <label className="font-mono-label mb-2 block text-zinc-500">
                  Phone
                </label>
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  data-testid={CONTACT.phone}
                  className={inputCls}
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+91 ..."
                />
              </div>
              <div className="sm:col-span-1">
                <label className="font-mono-label mb-2 block text-zinc-500">
                  Company
                </label>
                <input
                  data-testid={CONTACT.company}
                  className={inputCls}
                  value={form.company}
                  onChange={update("company")}
                  placeholder="Your organisation"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono-label mb-2 block text-zinc-500">
                  Service of interest
                </label>
                <Select
                  value={form.service}
                  onValueChange={(v) => setForm((s) => ({ ...s, service: v }))}
                >
                  <SelectTrigger
                    data-testid={CONTACT.service}
                    className="h-[52px] w-full rounded-lg border-white/10 bg-white/[0.03] px-4 text-left text-[14px] text-white transition-colors data-[state=open]:border-[var(--gold)]/60 focus:ring-0"
                  >
                    <SelectValue placeholder="Choose a service" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-[#0e0e10] text-white">
                    {SERVICES.map((s) => (
                      <SelectItem
                        key={s}
                        value={s}
                        className="focus:bg-[var(--gold)]/10 focus:text-[var(--gold-bright)]"
                      >
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono-label mb-2 block text-zinc-500">
                  Project brief*
                </label>
                <textarea
                  data-testid={CONTACT.message}
                  className={`${inputCls} min-h-[140px] resize-y ${errors.message ? "border-red-400/60" : ""}`}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="A few sentences about goals, timeline and budget range."
                  required
                />
                {errors.message && (
                  <p className="mt-1.5 text-[11.5px] text-red-400">{errors.message}</p>
                )}
              </div>
              <div className="sm:col-span-2 mt-2 flex flex-wrap items-center justify-between gap-4">
                <p className="text-[12px] text-zinc-500">
                  By submitting you agree to our privacy terms.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid={CONTACT.submit}
                  className="btn-gold disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Send Inquiry
                      <ArrowUpRight className="h-4 w-4" strokeWidth={2.2} />
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
